if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const renderCart = (cartState) => {
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        this.cart.renderContents(cartState);
                      });
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(cartState);
                });
              }
            };

            const bundleEbookVariantId = this.getBundleEbookVariantId();
            const mainVariantId = formData.get('id');

            if (!bundleEbookVariantId) {
              renderCart(response);
              return;
            }

            this.addBundleEbookToCart(bundleEbookVariantId, mainVariantId)
              .then(() => {
                if (this.cart && this.cart.tagName === 'CART-DRAWER') {
                  return this.fetchCartDrawerSections().then((stateWithSections) => {
                    renderCart(stateWithSections);
                  });
                }
                renderCart(response);
              })
              .catch((error) => {
                console.error('[Bundle ebook] add failed', error);
                renderCart(response);
              });
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      getBundleEbookVariantId() {
        if (this.dataset.bundleEbookVariantId) return this.dataset.bundleEbookVariantId;
        const sectionRoot = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
        if (!sectionRoot) return null;
        const whatsIncluded = sectionRoot.querySelector('.whats-included[data-has-free-ebook="true"]');
        if (!whatsIncluded) return null;
        return whatsIncluded.dataset.bundleEbookVariantId || null;
      }

      addBundleEbookToCart(ebookVariantId, mainVariantId) {
        const ebookVariantIdNumber = parseInt(ebookVariantId, 10);
        if (!ebookVariantIdNumber) {
          return Promise.reject(new Error('Invalid bundle ebook variant id'));
        }
        const normalizedRoute = (routes.cart_add_url || '/cart/add')
          .replace(/\?.*$/, '')
          .replace(/\/$/, '');
        const cartAddJsUrl = normalizedRoute.endsWith('/add') ? `${normalizedRoute}.js` : '/cart/add.js';

        return fetch(cartAddJsUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: ebookVariantIdNumber,
                quantity: 1,
                properties: { _bundle_variant_id: String(mainVariantId) },
              },
            ],
          }),
        })
          .then((res) => res.json())
          .then((payload) => {
            if (payload && payload.status) {
              throw new Error(payload.description || payload.message || 'Failed to add bundle ebook');
            }
            return payload;
          });
      }

      fetchCartDrawerSections() {
        const cartBaseUrl = routes.cart_url || '/cart';
        return Promise.all([
          fetch(`${cartBaseUrl}?section_id=cart-drawer`).then((r) => r.text()),
          fetch(`${cartBaseUrl}?section_id=cart-icon-bubble`).then((r) => r.text()),
        ]).then(([cartDrawerHtml, cartBubbleHtml]) => ({
          sections: {
            'cart-drawer': cartDrawerHtml,
            'cart-icon-bubble': cartBubbleHtml,
          },
        }));
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
